import React from 'react';
import { ComponentType } from 'react';

interface StatCardProps {
  title: string;
  value: number;
  icon: ComponentType<{ className?: string }>;
  color: 'blue' | 'green' | 'yellow' | 'purple' | 'indigo';
  description?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, color, description }) => {
  const colorClasses = {
    blue: {
      bg: 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20',
      border: 'border-blue-200 dark:border-blue-800',
      iconBg: 'bg-blue-500',
      iconText: 'text-white',
      valueText: 'text-blue-900 dark:text-blue-100',
      titleText: 'text-blue-700 dark:text-blue-300'
    },
    green: {
      bg: 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20',
      border: 'border-green-200 dark:border-green-800',
      iconBg: 'bg-green-500',
      iconText: 'text-white',
      valueText: 'text-green-900 dark:text-green-100',
      titleText: 'text-green-700 dark:text-green-300'
    },
    yellow: {
      bg: 'bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      iconBg: 'bg-yellow-500',
      iconText: 'text-white',
      valueText: 'text-yellow-900 dark:text-yellow-100',
      titleText: 'text-yellow-700 dark:text-yellow-300'
    },
    purple: {
      bg: 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20',
      border: 'border-purple-200 dark:border-purple-800',
      iconBg: 'bg-purple-500',
      iconText: 'text-white',
      valueText: 'text-purple-900 dark:text-purple-100',
      titleText: 'text-purple-700 dark:text-purple-300'
    },
    indigo: {
      bg: 'bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20',
      border: 'border-indigo-200 dark:border-indigo-800',
      iconBg: 'bg-indigo-500',
      iconText: 'text-white',
      valueText: 'text-indigo-900 dark:text-indigo-100',
      titleText: 'text-indigo-700 dark:text-indigo-300'
    }
  };

  const theme = colorClasses[color];

  return (
    <div className={`${theme.bg} rounded-xl border-2 ${theme.border} p-6 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className={`text-sm font-semibold ${theme.titleText} mb-2 uppercase tracking-wide`}>
            {title}
          </p>
          <p className={`text-4xl font-bold ${theme.valueText} mb-1`}>
            {value.toLocaleString()}
          </p>
          {description && (
            <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
          )}
        </div>
        <div className={`flex-shrink-0 p-3 rounded-xl ${theme.iconBg} shadow-md`}>
          <Icon className={`h-6 w-6 ${theme.iconText}`} />
        </div>
      </div>
      
      {/* Decorative element */}
      <div className="mt-4 h-1 w-full bg-background rounded-full opacity-30"></div>
    </div>
  );
};

export default StatCard;
